import ScheduleTick from "./ScheduleTick";

const uuidv4 = require('uuid/v4');

export default class Schedule {
	get beginDate() {
		return this.begin.date;
	}

	get endDate() {
		return this.end.date;
	}

	get beginTerm() {
		return this.begin.term;
	}

	get endTerm() {
		return this.end.term;
	}

	get ticks() {
		return ScheduleTick.msecToTicks(this.duration);
	}

	get days() {
		return ScheduleTick.msecToDays(this.duration);
	}

	get duration() {
		return (12 * 60 * 60 * 1000) + this.end.date.getTime() - this.begin.date.getTime();
	}
	
	constructor(params) {
		this.begin = params.begin;
		this.end = params.end;
		this.project_code = params.project_code;
		this.member_code = params.member_code;
		this.title = params.title === undefined ? '' : params.title;
		this.progress_rate = params.progress_rate === undefined ? null : params.progress_rate;
		this.color_code = params.color_code;
		
		if(params.code !== undefined)
			this.code = params.code;
		else
			this.code = uuidv4();
	}

	offsetDays(base_date) {
		return {
			begin: this.begin.offsetDays(base_date),
			end: this.end.offsetDays(base_date),
		};
	}

	offsetTicks(base_date) {
		return {
			begin: this.begin.offsetTicks(base_date),
			end: this.end.offsetTicks(base_date),
		};
	}

	clone() {
		return new Schedule({
			begin: this.begin.clone(),
			end: this.end.clone(),
			project_code: this.project_code,
			member_code: this.member_code,
			color_code: this.color_code,
		});
	}

	createOvers(other) {
		let overs = new Array();
		if (other.begin.code < this.begin.code) {
			overs.push(new Schedule({
				begin: new ScheduleTick({ code: other.begin.code }),
				end: new ScheduleTick({ code: this.begin.prevTickCode }),
				project_code: this.project_code,
				member_code: this.member_code,
				color_code: this.color_code,
			}));
		}
		if (this.end.code < other.end.code) {
			overs.push(new Schedule({
				begin: new ScheduleTick({ code: this.end.nextTickCode }),
				end: new ScheduleTick({ code: other.end.code }),
				project_code: this.project_code,
				member_code: this.member_code,
				color_code: this.color_code,
			}));
		}

		return overs;
	}

	createOverlap(other) {
		if (this.begin.code <= other.end.code && other.begin.code <= this.end.code) {
			return new Schedule({
				begin: new ScheduleTick({ code: Math.max(this.begin.code, other.begin.code) }), 
				end: new ScheduleTick({ code: Math.min(this.end.code, other.end.code) }), 
				project_code: this.project_code,
				member_code: this.member_code,
				color_code: this.color_code,
			});
		}
		return null;
	}

	createSubtract(other) {
		let remains = new Array();
		if ((this.begin.code <= other.begin.code) && (other.end.code <= this.end.code)) {
			if (this.begin.code != other.begin.code) {
				remains.push(new Schedule({
					begin: new ScheduleTick({ code: this.begin.code }),
					end: new ScheduleTick({ code: other.begin.prevTickCode }),
					project_code: this.project_code,
					member_code: this.member_code,
					color_code: this.color_code,
				}));
			}
			if (this.end.code != other.end.code) {
				remains.push(new Schedule({
					begin: new ScheduleTick({ code: other.end.nextTickCode }),
					end: new ScheduleTick({ code: this.end.code }),
					project_code: this.project_code,
					member_code: this.member_code,
					color_code: this.color_code,
				}));
			}
		} else {
			if (this.begin.code < other.begin.code) {
				remains.push(new Schedule({
					begin: new ScheduleTick({ code: this.begin.code }),
					end: new ScheduleTick({ code: Math.min(this.end.code, other.begin.prevTickCode) }),
					project_code: this.project_code,
					member_code: this.member_code,
					color_code: this.color_code,
				}));
			}
			if (other.end.code < this.end.code) {
				remains.push(new Schedule({
					begin: new ScheduleTick({ code: Math.max(this.begin.code, other.end.nextTickCode) }),
					end: new ScheduleTick({ code: this.end.code }),
					project_code: this.project_code,
					member_code: this.member_code,
					color_code: this.color_code,
				}));
			}
		}

		return remains;
	}

	merge(other) {
		if (this.begin.code <= other.end.code && other.begin.code <= this.end.code) {
			this.begin = new ScheduleTick({ code: Math.min(this.begin.code, other.begin.code) }); 
			this.end = new ScheduleTick({ code: Math.max(this.end.code, other.end.code) });
			return true;
		}
		return false;
	}

	anotherMerge(other) {
		let endNextTickCode = this.end.nextTickCode; 
		if (this.begin.code <= other.end.code && other.begin.code <= endNextTickCode) {
			this.begin = new ScheduleTick({ code: Math.min(this.begin.code, other.begin.code) }); 
			this.end = new ScheduleTick({ code: Math.max(this.end.code, other.end.code) });
			return true;
		}
		return false;
	}

	contains(other) {
		return (this.begin.code <= other.begin.code) && (other.end.code <= this.end.code);
	}

	containsTick(tick) {
		return this.begin.code <= tick.code && tick.code <= this.end.code;
	}
	
	asHash() {
		return {
			code: this.code,
			begin:{
				code: this.begin.code,
			},
			end: {
				code: this.end.code,
			},
			member_code: this.member_code,
			project_code: this.project_code,
			title: this.title,
			progress_rate: this.progress_rate,
			color_code: this.color_code,
		};
	}

	static compare(a, b) {
		let cmp = a.begin.code - b.begin.code;
		if (cmp != 0)
			return cmp;
		return a.end.code - b.end.code;
	}

	compare(other) {
		return Schedule.compare(this, other);
	}
}
